import {
  Table,
  Button,
  TextInput,
  NumberInput,
  Group,
  Center,
  Stack,
  ActionIcon,
  Checkbox,
} from '@mantine/core';
import { IconX, IconPlus, IconGripVertical } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useProposal } from '../../context/ProposalContext';
import { FEE_ACTIONS } from '../../reducers/feeReducer';
import { HourlyRate, HourlyRateRow } from '@/interfaces/fees';
import { v4 as uuidv4 } from 'uuid';
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface HourlyRateTableProps {
  fee: HourlyRate;
  index: number;
}

function SortableRow({
  row,
  rowIndex,
  onUpdate,
  onRemove,
}: {
  row: HourlyRateRow;
  rowIndex: number;
  onUpdate: (rowIndex: number, updatedRow: HourlyRateRow) => void;
  onRemove: (rowIndex: number) => void;
}) {
  const { t } = useTranslation();
  const { setNodeRef, transform, transition, isDragging, listeners, attributes } = useSortable({
    id: rowIndex,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
    height: '100%',
    display: 'table-row',
  };

  return (
    <Table.Tr ref={setNodeRef} style={style}>
      <Table.Td>
        <div style={{ cursor: 'grab', padding: '0.5rem' }} {...listeners} {...attributes}>
          <IconGripVertical size={20} />
        </div>
      </Table.Td>
      <Table.Td>
        <TextInput
          value={row.position}
          onChange={(e) => onUpdate(rowIndex, { ...row, position: e.target.value })}
        />
      </Table.Td>
      <Table.Td>
        <NumberInput
          value={row.rate}
          onChange={(value) => onUpdate(rowIndex, { ...row, rate: Number(value) })}
        />
      </Table.Td>
      <Table.Td>
        <ActionIcon variant="subtle" color="red" onClick={() => onRemove(rowIndex)}>
          <IconX size={20} />
        </ActionIcon>
      </Table.Td>
    </Table.Tr>
  );
}

export default function HourlyRateTable({ fee, index }: HourlyRateTableProps) {
  const { t } = useTranslation();
  const { dispatchProposal } = useProposal();

  const handleAddRow = () => {
    const newRow: HourlyRateRow = {
      id: uuidv4(),
      position: '',
      rate: 0,
    };
    dispatchProposal({
      type: FEE_ACTIONS.ADD_TABLE_ROW,
      index,
      payload: newRow,
    });
  };

  const handleRemoveRow = (rowIndex: number) => {
    dispatchProposal({
      type: FEE_ACTIONS.REMOVE_TABLE_ROW,
      index,
      tableIndex: rowIndex,
    });
  };

  const handleUpdateRow = (rowIndex: number, updatedRow: HourlyRateRow) => {
    dispatchProposal({
      type: FEE_ACTIONS.UPDATE_TABLE_ROW,
      index,
      tableIndex: rowIndex,
      payload: updatedRow,
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = active.id as number;
    const newIndex = over.id as number;

    const reorderedRows = arrayMove(fee.table, oldIndex, newIndex);

    dispatchProposal({
      type: FEE_ACTIONS.REORDER_TABLE_ROWS,
      index,
      payload: reorderedRows,
    });
  };

  const handleBlendedRateChange = (checked: boolean) => {
    dispatchProposal({
      type: FEE_ACTIONS.UPDATE_FEE,
      index,
      payload: {
        ...fee,
        blendedRate: checked ? 0 : null,
      },
    });
  };

  const handleBlendedRateValueChange = (value: number) => {
    dispatchProposal({
      type: FEE_ACTIONS.UPDATE_FEE,
      index,
      payload: {
        ...fee,
        blendedRate: value,
      },
    });
  };

  return (
    <Stack>
      <DndContext onDragEnd={handleDragEnd}>
        <SortableContext
          items={fee.table.map((_, index) => index)}
          strategy={verticalListSortingStrategy}
        >
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th></Table.Th>
                <Table.Th>{t('position')}</Table.Th>
                <Table.Th>{t('rate')}</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {fee.table.map((row, rowIndex) => (
                <SortableRow
                  key={row.id}
                  row={row}
                  rowIndex={rowIndex}
                  onUpdate={handleUpdateRow}
                  onRemove={handleRemoveRow}
                />
              ))}
            </Table.Tbody>
          </Table>
        </SortableContext>
      </DndContext>
      <Group justify="space-between">
        <Group ml="lg" mb="lg" align="center">
          <Checkbox
            label={t('addBlendedRate')}
            onChange={(event) => handleBlendedRateChange(event.currentTarget.checked)}
            checked={fee.blendedRate !== null}
          />
          {fee.blendedRate !== null && (
            <NumberInput
              label={t('rate')}
              value={fee.blendedRate}
              onChange={(value) => handleBlendedRateValueChange(Number(value))}
              min={0}
              step={10}
              w={150}
            />
          )}
        </Group>
        <Button variant="light" leftSection={<IconPlus size={14} />} onClick={handleAddRow}>
          {t('addRow')}
        </Button>
      </Group>
    </Stack>
  );
}
